import { createPart, type PagePart, type PartType } from '@/features/storefront/sections/parts';
import { defaultSection } from '@/features/storefront/sections/pages';
import { createSection, type Section } from '@/features/storefront/sections/schema';

/**
 * Lumera's product page, written down.
 *
 * A beauty product page is quiet: a large square photograph on the left, and
 * on the right the name, one line on what it is for, the price with what it
 * used to cost, and a single wide "Add to cart". Everything longer - what is in
 * it, how to use it - folds away under "Product details" so the buy column
 * stays short. Under the product sit the reviews and the rest of the range.
 *
 * As with Hyper's, these are *defaults*: what a merchant who has never
 * arranged the page gets, every part and section editable, movable and
 * removable in the customiser. And as with Hyper's, they carry nothing that
 * belongs to the sample shop - no photographs, no quotes, no offer - only
 * bands that draw the merchant's own data.
 */

/**
 * One component of the page, with an id that does not change between renders
 * - see Hyper's `productPage.ts` for why a default must not mint a fresh id.
 */
function part(
  id: string,
  type: PartType,
  region: string,
  over: Partial<Omit<PagePart, 'id' | 'type'>> = {},
): PagePart {
  const base = createPart(type, region);
  return {
    ...base,
    ...over,
    id: `lumera-${id}`,
    region,
    options: { ...base.options, ...(over.options ?? {}) },
  };
}

/** The components of the product, in the regions `Product.tsx` lays out. */
export function lumeraProductParts(): PagePart[] {
  return [
    part('breadcrumbs', 'product_breadcrumbs', 'top'),

    part('gallery', 'product_gallery', 'gallery'),

    part('title', 'product_title', 'main'),
    part('summary', 'product_short_description', 'main'),
    part('price', 'product_price', 'main', {
      options: { show_compare_at: true, show_saving: true },
    }),
    part('variants', 'product_variants', 'main'),
    part('quantity', 'product_quantity', 'main'),
    part('add', 'product_add_to_cart', 'main', { width: 'full' }),
    part('share', 'product_share', 'main'),
    part('description', 'product_description', 'main', {
      label: 'Product details',
      space_above: 'lg',
      options: { show_heading: true, collapsible: true },
    }),
    part('specs', 'product_specs', 'main', {
      label: 'Specifications',
      options: { collapsible: true },
    }),

    part('related', 'product_related', 'below'),
  ];
}

/** A section with a stable id, for the same reason {@link part} fixes one. */
function fixed(id: string, section: Section): Section {
  return { ...section, id: `lumera-${id}` };
}

/**
 * The product page as a merchant on this theme first finds it: the product -
 * with its suggestions already under it, see `product_related` above - then
 * its ratings and reviews on the page's soft surface.
 */
export function lumeraProductSections(): Section[] {
  return [
    defaultSection('product_details'),
    fixed('reviews', {
      ...createSection('product_reviews'),
      title: 'Ratings & reviews',
      background: 'surface',
      padding: 'lg',
    }),
  ];
}
